import { useState, useEffect, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'
import { api } from '../lib/api'
import { toast } from 'react-toastify'
import { Check, Circle, Loader2, ArrowRight, TrendingUp, AlertCircle, CheckCircle2 } from 'lucide-react'
import { useWorkerProgress } from '../hooks/useWorkerProgress'
import BrandConfirmationStep from '../components/onboarding/BrandConfirmationStep'
import type { BusinessScope } from '../types/brand'
import { trackEvent, ANALYTICS_EVENTS } from '../lib/analytics'

type StepStatus = 'idle' | 'processing' | 'ready' | 'completed' | 'error'

interface OnboardingStep {
  id: string
  title: string
  description: string
  requiresUserInput: boolean
  status: StepStatus
  error?: string
}

const INITIAL_STEPS: OnboardingStep[] = [
  {
    id: 'brand-learning',
    title: 'Learning about your Brand...',
    description: 'This only takes a few minutes. Hold tight.',
    requiresUserInput: false,
    status: 'idle'
  },
  {
    id: 'confirm-brand',
    title: 'Confirm Your Brand',
    description: 'Review your brand details and define your market area.',
    requiresUserInput: true,
    status: 'idle'
  },
  {
    id: 'select-queries',
    title: 'Select Search Query Types',
    description: 'Choose 3-5 search queries you\'d want your brand to show in. You can change and write your own later.',
    requiresUserInput: true,
    status: 'idle'
  },
  {
    id: 'analyze-queries',
    title: 'Analyzing AI search...',
    description: 'We are investigating search patterns, your brands positioning, and relevant competition',
    requiresUserInput: false,
    status: 'idle'
  },
  {
    id: 'select-competitors',
    title: 'Select Competitor Brands',
    description: 'Pick up to 3 brands for head-to-head analysis. You can change these later and add your own.',
    requiresUserInput: true,
    status: 'idle'
  },
  {
    id: 'final-analysis',
    title: 'Running Brand Analysis & Search Intelligence...',
    description: 'Finishing things up so we can present your complete BrandIndex Score',
    requiresUserInput: false,
    status: 'idle'
  }
]

export default function Onboarding() {
  const navigate = useNavigate()
  const { activeBrand } = useAuth()
  const [currentStep, setCurrentStep] = useState(0)
  const [steps, setSteps] = useState<OnboardingStep[]>(INITIAL_STEPS)
  const [availableProviders, setAvailableProviders] = useState<Array<{
    provider: string
    displayName: string
    available: boolean
  }>>([])

  // Guard: Require valid subscription to access onboarding
  // This must complete BEFORE any initialization or step processing begins
  useEffect(() => {
    if (activeBrand) {
      const validStatuses = ['TRIAL', 'ACTIVE']
      if (!activeBrand.subscriptionStatus || !validStatuses.includes(activeBrand.subscriptionStatus)) {
        console.warn('Onboarding access denied: No valid subscription', {
          brandId: activeBrand.id,
          subscriptionStatus: activeBrand.subscriptionStatus
        })
        setSubscriptionValid(false)
        toast.error('Please select a subscription plan to continue')
        navigate('/settings/brands')
      } else {
        setSubscriptionValid(true)
      }
    }
  }, [activeBrand, navigate])

  // Step-specific state
  // Brand profile from Step 0
  const [brandProfile, setBrandProfile] = useState<{
    companyName: string
    description: string
  }>({ companyName: '', description: '' })

  const [businessScope, setBusinessScope] = useState<BusinessScope>('NATIONAL')
  const [serviceAreas, setServiceAreas] = useState<string[]>([])
  const [primaryLocation, setPrimaryLocation] = useState<string>()
  const [recommendedQueries, setRecommendedQueries] = useState<string[]>([])
  const [selectedQueries, setSelectedQueries] = useState<Set<number>>(new Set())
  const [competitors, setCompetitors] = useState<any[]>([])
  const [selectedCompetitors, setSelectedCompetitors] = useState<Set<string>>(new Set())
  const [analysisProgress, setAnalysisProgress] = useState({ current: 0, total: 100, task: '' })
  const [analysisUIStep, setAnalysisUIStep] = useState(0) // UI animation step (0-5)
  const analysisUIStepRef = useRef(0) // Track UI step in ref for interval
  const [queryAnalysisProgress, setQueryAnalysisProgress] = useState<{
    queries: Array<{ query: string; providers: Record<string, boolean> }>
    currentQuery: number
    currentProvider: string
  }>({ queries: [], currentQuery: -1, currentProvider: '' })

  // Prevent double execution in React Strict Mode
  const stepProcessingStarted = useRef<Set<number>>(new Set())
  const hasInitialized = useRef(false)

  // Track subscription validation state (null = checking, true = valid, false = invalid)
  const [subscriptionValid, setSubscriptionValid] = useState<boolean | null>(null)

  // Content import progress tracking (background process)
  const importProgress = useWorkerProgress({
    brandId: (activeBrand?.brandId || activeBrand?.id) || '',
    statusEndpoint: '/content/import-status',
    pollInterval: 3000,
    pollTimeout: 120000,
    completionMessage: 'Content import complete!',
    showToast: false
  })

  // Fetch available AI providers on mount
  useEffect(() => {
    const fetchProviders = async () => {
      try {
        const response = await api.get('/search/providers')
        const providers = response.data.providers
          .filter((p: any) => p.available)
          .map((p: any) => ({
            provider: p.provider,
            displayName: getProviderDisplayName(p.provider),
            available: p.available
          }))
        setAvailableProviders(providers)
        console.log('[Onboarding] Available providers:', providers.map((p: any) => p.provider))
      } catch (error) {
        console.error('[Onboarding] Failed to fetch providers:', error)
        // Fallback to default providers if fetch fails
        setAvailableProviders([
          { provider: 'openai', displayName: 'OpenAI', available: true },
          { provider: 'anthropic', displayName: 'Anthropic', available: true },
          { provider: 'gemini', displayName: 'Gemini', available: true }
        ])
      }
    }
    fetchProviders()
  }, [])

  // Helper function to get display name for provider
  const getProviderDisplayName = (provider: string): string => {
    const displayNames: Record<string, string> = {
      'openai': 'OpenAI',
      'anthropic': 'Anthropic',
      'gemini': 'Gemini',
      'perplexity': 'Perplexity'
    }
    return displayNames[provider] || provider.charAt(0).toUpperCase() + provider.slice(1)
  }

  // Initialize: Check if onboarding already completed
  // Only runs AFTER subscription is validated to prevent premature API calls
  useEffect(() => {
    if (subscriptionValid !== true) return // Wait for subscription validation

    if (activeBrand?.userBrandId && !hasInitialized.current) {
      hasInitialized.current = true
      // Track onboarding started
      trackEvent(ANALYTICS_EVENTS.ONBOARDING_STARTED, {
        brandId: activeBrand.brandId || activeBrand.id,
        industry: activeBrand.industry
      })
      checkIfAlreadyCompleted()
    }
  }, [activeBrand?.userBrandId, subscriptionValid])

  // Main step processor - runs when currentStep changes
  // Only runs AFTER subscription is validated to prevent premature API calls
  useEffect(() => {
    if (!activeBrand?.id) return
    if (subscriptionValid !== true) return // Wait for subscription validation

    const stepIndex = currentStep
    const step = steps[stepIndex]

    // Prevent double execution
    if (stepProcessingStarted.current.has(stepIndex)) return

    // Only process if step is idle
    if (step?.status === 'idle') {
      stepProcessingStarted.current.add(stepIndex)
      processStep(stepIndex)
    }
  }, [currentStep, activeBrand?.id, subscriptionValid])

  // Poll for content stats (background info only) - removed as unused

  const checkIfAlreadyCompleted = async () => {
    try {
      const response = await api.get(`/onboarding/progress/${activeBrand?.userBrandId}`)
      const savedProgress = response.data

      // If completed, redirect to dashboard
      if (savedProgress.completed) {
        console.log('[Onboarding] Already completed, redirecting to dashboard')
        navigate('/dashboard')
        return
      }

      // If in progress, restore to saved step
      if (savedProgress.currentStep !== undefined && savedProgress.currentStep > 0) {
        const savedStep = savedProgress.currentStep
        console.log(`[Onboarding] Restoring progress to step ${savedStep}`)

        // Mark all previous steps as completed
        setSteps(prev => prev.map((step, idx) => ({
          ...step,
          status: idx < savedStep ? 'completed' : step.status
        })))

        // Set current step (this will trigger the step processor)
        setCurrentStep(savedStep)
      } else {
        console.log('[Onboarding] Starting from beginning')
      }
    } catch (error) {
      console.log('[Onboarding] No saved progress, starting fresh')
    }
  }

  const updateStepStatus = (stepIndex: number, status: StepStatus, error?: string) => {
    console.log(`[Onboarding] Step ${stepIndex} (${steps[stepIndex]?.id}): ${status}`)
    setSteps(prev => prev.map((step, idx) =>
      idx === stepIndex ? { ...step, status, error } : step
    ))

    // Track step completion
    if (status === 'completed') {
      trackEvent(ANALYTICS_EVENTS.ONBOARDING_STEP_COMPLETED, {
        stepIndex,
        stepId: steps[stepIndex]?.id,
        stepTitle: steps[stepIndex]?.title
      })
    }
  }

  const saveProgress = async (stepIndex: number, completed: boolean = false) => {
    try {
      await api.put(`/onboarding/progress/${activeBrand?.userBrandId}`, {
        currentStep: stepIndex,
        completed
      })
    } catch (error) {
      console.error('[Onboarding] Failed to save progress:', error)
    }
  }

  const advanceToNextStep = (fromStep?: number) => {
    setCurrentStep(prev => {
      // If fromStep is provided, only advance if we're still on that step (prevent race conditions)
      if (fromStep !== undefined && prev !== fromStep) {
        console.warn(`[Onboarding] Blocked stale advancement: tried to advance from step ${fromStep} but currently on step ${prev}`)
        return prev
      }

      const nextStep = prev + 1
      console.log(`[Onboarding] Advancing from step ${prev} to ${nextStep}`)

      // Save progress for the new step
      saveProgress(nextStep)
      return nextStep
    })
  }

  // ============================================================================
  // STEP PROCESSORS - Each step has its own handler
  // ============================================================================

  const processStep = async (stepIndex: number) => {
    const step = steps[stepIndex]
    console.log(`[Onboarding] Processing step ${stepIndex}: ${step.id}`)

    try {
      updateStepStatus(stepIndex, 'processing')

      switch (step.id) {
        case 'brand-learning':
          await processBrandLearning()
          break
        case 'confirm-brand':
          await processConfirmBrand()
          break
        case 'select-queries':
          await processQuerySelection()
          break
        case 'analyze-queries':
          await processQueryAnalysis()
          break
        case 'select-competitors':
          await processCompetitorSelection()
          break
        case 'final-analysis':
          await processFinalAnalysis()
          break
      }
    } catch (error: any) {
      console.error(`[Onboarding] Error in step ${stepIndex}:`, error)
      updateStepStatus(stepIndex, 'error', error.message)
      toast.error(`Step failed: ${error.message}`)
    }
  }

  // STEP 0: Brand Learning (Auto-advance)
  const processBrandLearning = async () => {
    try {
      console.log('[Onboarding] Starting brand profile generation...')

      // Check if content already exists
      const contentRes = await api.get(`/content/stats/${activeBrand?.brandId || activeBrand?.id}`)
      const hasContent = contentRes.data.total > 0

      // Start content import if no content exists (worker handles accessibility detection)
      if (!hasContent) {
        // Start content import in background (non-blocking)
        // The worker will detect accessibility and update the brand record
        console.log('[Onboarding] Starting background content import...')
        startContentImport()
      }

      // Generate brand profile (fast ~5-10s)
      const response = await api.post(`/onboarding/${activeBrand?.userBrandId}/generate-profile`)

      if (response.data.success) {
        console.log('[Onboarding] Brand profile generated successfully')

        // Store the profile for the next step
        const profile = response.data.profile
        setBrandProfile({
          companyName: profile?.companyName || activeBrand?.companyName || '',
          description: profile?.description || activeBrand?.description || ''
        })

        updateStepStatus(0, 'completed')

        // Auto-advance after brief delay
        setTimeout(() => advanceToNextStep(0), 800)
      } else {
        throw new Error('Profile generation returned unsuccessful')
      }
    } catch (error: any) {
      console.error('[Onboarding] Brand learning failed:', error)

      // Use existing brand data as fallback
      setBrandProfile({
        companyName: activeBrand?.companyName || '',
        description: activeBrand?.description || ''
      })

      // Don't block onboarding - continue with limited data
      updateStepStatus(0, 'completed')
      setTimeout(() => advanceToNextStep(0), 800)
    }
  }

  const startContentImport = async () => {
    try {
      await api.post('/content/import-domain/queue', {
        brandId: activeBrand?.brandId || activeBrand?.id,
        domain: activeBrand?.domain,
        options: {
          maxPages: 50,
          skipRobotsTxt: false
        }
      })
      importProgress.startPolling('Importing content from your website...')
    } catch (error) {
      console.error('[Onboarding] Content import failed:', error)
    }
  }

  // STEP 1: Confirm Brand (Wait for user input)
  const processConfirmBrand = async () => {
    try {
      // Fetch existing brand data to pre-populate
      const brandRes = await api.get(`/brands/${activeBrand?.brandId || activeBrand?.id}`)
      const brand = brandRes.data

      // Update brand profile if not already set from Step 0
      if (!brandProfile.companyName && brand.companyName) {
        setBrandProfile(prev => ({
          ...prev,
          companyName: brand.companyName
        }))
      }
      if (!brandProfile.description && brand.description) {
        setBrandProfile(prev => ({
          ...prev,
          description: brand.description
        }))
      }

      if (brand.businessScope) {
        setBusinessScope(brand.businessScope as BusinessScope)
      }
      if (brand.serviceAreas && brand.serviceAreas.length > 0) {
        setServiceAreas(brand.serviceAreas)
      }
      if (brand.primaryLocation) {
        setPrimaryLocation(brand.primaryLocation)
      }

      // Mark as ready - wait for user to save
      updateStepStatus(1, 'ready')
    } catch (error: any) {
      console.error('[Onboarding] Failed to fetch brand data:', error)
      // Still mark as ready even if fetch fails - use defaults
      updateStepStatus(1, 'ready')
    }
  }

  const handleSaveConfirmBrand = async (data: {
    companyName: string
    description: string
    businessScope: BusinessScope
    serviceAreas: string[]
    primaryLocation?: string
  }) => {
    try {
      updateStepStatus(1, 'processing')

      // 1. Save brand info + business scope
      await api.patch(`/brands/${activeBrand?.brandId || activeBrand?.id}`, {
        companyName: data.companyName,
        description: data.description,
        businessScope: data.businessScope,
        serviceAreas: data.serviceAreas,
        primaryLocation: data.primaryLocation
      })

      // Update local state
      setBrandProfile({
        companyName: data.companyName,
        description: data.description
      })
      setBusinessScope(data.businessScope)
      setServiceAreas(data.serviceAreas)
      setPrimaryLocation(data.primaryLocation)

      // 2. Discover industry (runs in background, doesn't block)
      console.log('[Onboarding] Discovering industry...')
      api.post(`/onboarding/${activeBrand?.userBrandId}/discover-industry`)
        .then((res) => console.log('[Onboarding] Industry discovered:', res.data.industryName))
        .catch((err) => console.warn('[Onboarding] Industry discovery failed:', err))

      // 3. Generate queries (needed for next step)
      console.log('[Onboarding] Generating queries...')
      const queryRes = await api.post(`/brand-profile/${activeBrand?.brandId || activeBrand?.id}/queries`, {
        count: 8,
        includeReasoning: false
      })

      if (queryRes.data.success && queryRes.data.queries?.length > 0) {
        console.log('[Onboarding] Generated', queryRes.data.queries.length, 'smart queries')
        setRecommendedQueries(queryRes.data.queries)
        setSelectedQueries(new Set([0, 1, 2])) // Auto-select first 3
      }

      updateStepStatus(1, 'completed')
      setTimeout(() => advanceToNextStep(1), 500)
    } catch (error: any) {
      console.error('[Onboarding] Failed to save brand confirmation:', error)
      updateStepStatus(1, 'error', error.message)
      toast.error('Failed to save brand details')
    }
  }

  // STEP 2: Query Selection (Wait for user input)
  const processQuerySelection = async () => {
    try {
      // Queries were already generated in Step 1, check if we have them
      if (recommendedQueries.length > 0) {
        console.log('[Onboarding] Using queries from Step 1:', recommendedQueries.length)
        updateStepStatus(2, 'ready')
        return
      }

      // If no queries (e.g., page refresh), generate them now
      console.log('[Onboarding] Generating query recommendations...')

      const response = await api.post(`/brand-profile/${activeBrand?.brandId || activeBrand?.id}/queries`, {
        count: 8,
        includeReasoning: false
      })

      if (response.data.success && response.data.queries?.length > 0) {
        console.log('[Onboarding] Generated', response.data.queries.length, 'smart queries')
        setRecommendedQueries(response.data.queries)
        setSelectedQueries(new Set([0, 1, 2])) // Auto-select first 3
      } else {
        // Fallback to template queries
        throw new Error('No queries from API')
      }

      // Mark as ready - wait for user to click button
      updateStepStatus(2, 'ready')
    } catch (error: any) {
      console.error('[Onboarding] Query generation failed, using templates:', error)

      // Fallback queries
      const brandName = brandProfile.companyName || activeBrand?.companyName || 'our company'
      const industry = activeBrand?.industry || 'our industry'

      setRecommendedQueries([
        `Best ${industry.toLowerCase()} companies`,
        `Top solutions for ${industry.toLowerCase()}`,
        `${brandName} alternatives`,
        `${industry.toLowerCase()} comparison`,
        `Leading ${industry.toLowerCase()} brands`
      ])
      setSelectedQueries(new Set([0, 1, 2]))

      updateStepStatus(2, 'ready')
    }
  }

  const handleCreateQueries = async () => {
    if (selectedQueries.size < 3) {
      toast.warning('Please select at least 3 queries')
      return
    }

    try {
      updateStepStatus(2, 'processing')

      const brandName = activeBrand?.companyName || 'our company'
      const queriesToCreate = Array.from(selectedQueries).map(i => recommendedQueries[i])

      for (const query of queriesToCreate) {
        await api.post('/search/query', {
          brandId: activeBrand?.brandId || activeBrand?.id,
          query,
          targetKeywords: [],
          expectedMention: brandName,
          isActive: true
        })
        await new Promise(resolve => setTimeout(resolve, 300))
      }

      // Start intelligence processing early (runs in background while user continues)
      // This saves 30-60s from Step 5 since intelligence will already be processing
      console.log('[Onboarding] Starting early intelligence processing...')
      api.post(`/onboarding/${activeBrand?.userBrandId}/start-intelligence`)
        .then(() => console.log('[Onboarding] Intelligence processing started in background'))
        .catch((err) => console.warn('[Onboarding] Failed to start early intelligence:', err))

      updateStepStatus(2, 'completed')
      setTimeout(() => advanceToNextStep(2), 500)
    } catch (error: any) {
      console.error('[Onboarding] Failed to create queries:', error)
      updateStepStatus(2, 'error', error.message)
      toast.error('Failed to create queries')
    }
  }

  // STEP 3: Query Analysis (Auto-advance)
  const processQueryAnalysis = async () => {
    try {
      console.log('[Onboarding] Starting query analysis...')

      // Fetch queries
      const queriesRes = await api.get('/search/queries', {
        params: { brandId: activeBrand?.brandId || activeBrand?.id, limit: 10 }
      })
      const queries = queriesRes.data?.queries || []

      if (queries.length === 0) {
        throw new Error('No queries found to analyze')
      }

      // Analyze all queries that were created (up to 10)
      const queriesToAnalyze = queries

      // Initialize progress tracking with dynamic providers
      const providerTracking = availableProviders.reduce((acc, p) => {
        acc[p.provider] = false
        return acc
      }, {} as Record<string, boolean>)

      setQueryAnalysisProgress({
        queries: queriesToAnalyze.map((q: any) => ({
          query: q.query,
          providers: { ...providerTracking }
        })),
        currentQuery: 0,
        currentProvider: availableProviders[0]?.displayName || 'AI Provider'
      })

      // Queue all existing queries for analysis with dynamic providers
      const providerIds = availableProviders.map(p => p.provider)
      const jobIds: string[] = []
      for (const query of queriesToAnalyze) {
        try {
          const response = await api.post(`/search/query/${query.id}/queue`, {
            providers: providerIds,
            parallel: true,
            priority: 'normal'
          })
          jobIds.push(response.data.jobId)
          console.log(`[Onboarding] Queued query "${query.query}" (job: ${response.data.jobId})`)
        } catch (error) {
          console.warn(`[Onboarding] Failed to queue "${query.query}":`, error)
        }
      }

      // NEW: Track completion for early advancement
      const minQueriesForCompetitors = Math.min(3, queriesToAnalyze.length)
      let completedQueries = 0
      let competitorsExtracted = false

      // Helper function to check if query is complete (moved outside loop)
      const checkQueryComplete = async (queryId: string) => {
        try {
          const statusRes = await api.get(`/search/query/${queryId}`)
          const hasResults = statusRes.data?.searchResults?.length > 0
          const hasErrors = statusRes.data?.searchResults?.some((r: any) => r.error)
          return hasResults && !hasErrors
        } catch (error) {
          return false
        }
      }

      // Monitor progress for each query - with early exit
      for (let i = 0; i < queriesToAnalyze.length; i++) {
        const query = queriesToAnalyze[i]
        console.log(`[Onboarding] Monitoring query ${i + 1}: "${query.query}"`)

        // Update current query
        setQueryAnalysisProgress(prev => ({
          ...prev,
          currentQuery: i,
          currentProvider: availableProviders[0]?.displayName || 'AI Provider'
        }))

        // Simulate provider progress for better UX (providers run in parallel in background)
        const providers = availableProviders.map(p => p.provider)
        const providerNames = availableProviders.map(p => p.displayName)

        let queryComplete = false
        const startTime = Date.now()
        const maxWaitTime = 60000 // 60 seconds max

        for (let p = 0; p < providers.length; p++) {
          setQueryAnalysisProgress(prev => ({
            ...prev,
            currentQuery: i,
            currentProvider: providerNames[p]
          }))

          // Wait 2 seconds OR until query completes, whichever comes first
          const waitUntil = Date.now() + 2000
          while (Date.now() < waitUntil && !queryComplete) {
            queryComplete = await checkQueryComplete(query.id)
            if (!queryComplete) {
              await new Promise(resolve => setTimeout(resolve, 500))
            }
          }

          // Mark provider as complete in UI
          setQueryAnalysisProgress(prev => ({
            ...prev,
            queries: prev.queries.map((q, idx) =>
              idx === i
                ? { ...q, providers: { ...q.providers, [providers[p]]: true } }
                : q
            )
          }))

          // If query finished early, mark all remaining providers as complete and break
          if (queryComplete) {
            console.log(`[Onboarding] Query "${query.query}" completed during ${providerNames[p]}`)

            // Mark all remaining providers as complete
            setQueryAnalysisProgress(prev => ({
              ...prev,
              queries: prev.queries.map((q, idx) =>
                idx === i
                  ? { ...q, providers: { openai: true, google: true, anthropic: true } }
                  : q
              )
            }))
            break
          }
        }

        // If still not complete after UI animation, continue polling
        while (!queryComplete && (Date.now() - startTime) < maxWaitTime) {
          await new Promise(resolve => setTimeout(resolve, 1000))
          queryComplete = await checkQueryComplete(query.id)
        }

        if (queryComplete) {
          completedQueries++
          console.log(`[Onboarding] Completed ${completedQueries}/${queriesToAnalyze.length} queries`)

          // Extract competitors EARLY - as soon as we have minimum queries
          if (!competitorsExtracted && completedQueries >= minQueriesForCompetitors) {
            console.log(`[Onboarding] Ready to extract competitors from ${completedQueries} completed queries`)
            competitorsExtracted = true
          }
        } else {
          console.warn(`[Onboarding] Query "${query.query}" timed out after ${maxWaitTime/1000}s, continuing anyway`)
        }

        // Ensure all providers are marked complete at the end
        setQueryAnalysisProgress(prev => ({
          ...prev,
          queries: prev.queries.map((q, idx) =>
            idx === i
              ? { ...q, providers: { openai: true, google: true, anthropic: true } }
              : q
          )
        }))

        // EARLY EXIT: Advance to competitor selection once we have enough queries
        // Remaining queries continue processing in background
        if (competitorsExtracted && completedQueries >= minQueriesForCompetitors) {
          console.log(`[Onboarding] Advancing early with ${completedQueries}/${queriesToAnalyze.length} queries complete`)
          console.log(`[Onboarding] ${queriesToAnalyze.length - completedQueries} queries will continue in background`)

          setQueryAnalysisProgress(prev => ({
            ...prev,
            currentQuery: queriesToAnalyze.length,
            currentProvider: 'Extracting competitors...'
          }))
          await new Promise(resolve => setTimeout(resolve, 1500))

          updateStepStatus(3, 'completed')
          setTimeout(() => advanceToNextStep(3), 800)
          return // Exit early!
        }
      }

      // Fallback: All queries processed
      console.log('[Onboarding] All queries processed')
      updateStepStatus(3, 'completed')
      setTimeout(() => advanceToNextStep(3), 800)
    } catch (error: any) {
      console.error('[Onboarding] Query analysis failed:', error)
      updateStepStatus(3, 'error', error.message)

      // Allow user to skip or retry
      toast.error('Query analysis failed. You can skip this step.')
    }
  }

  // STEP 4: Competitor Selection (Wait for user input)
  const processCompetitorSelection = async () => {
    try {
      console.log('[Onboarding] Checking for existing competitors...')
      console.log('[Onboarding] Using brandId:', activeBrand?.brandId || activeBrand?.id)

      // Always regenerate competitors to pick up newly discovered brands from query analysis
      console.log('[Onboarding] Generating competitors from analyzed queries...')
      const generateRes = await api.post(`/competitors/${activeBrand?.brandId || activeBrand?.id}/generate`)
      console.log('[Onboarding] Generate response:', generateRes.data)

      // Wait 1 second for database writes to complete
      console.log('[Onboarding] Waiting for database writes to complete...')
      await new Promise(resolve => setTimeout(resolve, 1000))

      // Fetch the updated competitor list
      const competitorsRes = await api.get(`/competitors/${activeBrand?.brandId || activeBrand?.id}`)
      console.log('[Onboarding] GET competitors after generate:', competitorsRes.data)
      const competitorsList = competitorsRes.data.competitors || []

      console.log(`[Onboarding] Found ${competitorsList.length} competitors total`)
      console.log('[Onboarding] Competitors array:', competitorsList.map((c: any) => ({ id: c.id, name: c.name })))

      setCompetitors(competitorsList)

      if (competitorsList.length === 0) {
        console.warn('[Onboarding] No competitors available')
        updateStepStatus(4, 'ready') // Still mark as ready, show skip option
      } else {
        // Check if any competitors are already marked as head-to-head
        const headToHeadCompetitors = competitorsList.filter((c: any) => c.isHeadToHead)

        if (headToHeadCompetitors.length > 0) {
          // Restore previous selections
          console.log(`[Onboarding] Restoring ${headToHeadCompetitors.length} previously selected competitors`)
          const selected = new Set<string>(headToHeadCompetitors.map((c: any) => c.id))
          setSelectedCompetitors(selected)
        } else {
          // Auto-select up to 3 competitors (or all if fewer than 3)
          const autoSelectCount = Math.min(3, competitorsList.length)
          const autoSelect = competitorsList.slice(0, autoSelectCount).map((c: any) => c.id)
          console.log(`[Onboarding] Auto-selecting ${autoSelectCount} competitor(s)`)
          setSelectedCompetitors(new Set(autoSelect))
        }

        updateStepStatus(4, 'ready')
      }
    } catch (error: any) {
      console.error('[Onboarding] Competitor processing failed:', error)
      updateStepStatus(4, 'error', error.message)
    }
  }

  const handleSaveCompetitors = async () => {
    // Validation: Require selecting all available competitors if < 3, otherwise exactly 3
    const requiredCount = Math.min(3, competitors.length)
    if (selectedCompetitors.size !== requiredCount) {
      if (competitors.length < 3) {
        toast.warning(`Please select all ${competitors.length} available competitor${competitors.length === 1 ? '' : 's'}`)
      } else {
        toast.warning('Please select exactly 3 competitors')
      }
      return
    }

    try {
      updateStepStatus(4, 'processing')

      // Update each competitor's head-to-head status
      for (const competitor of competitors) {
        const isHeadToHead = selectedCompetitors.has(competitor.id)
        await api.patch(
          `/competitors/${activeBrand?.brandId || activeBrand?.id}/${competitor.id}/head-to-head`,
          { isHeadToHead }
        )
      }

      updateStepStatus(4, 'completed')
      setTimeout(() => advanceToNextStep(4), 500)
    } catch (error: any) {
      console.error('[Onboarding] Failed to save competitors:', error)
      updateStepStatus(4, 'error', error.message)
      toast.error('Failed to save competitors')
    }
  }

  const handleSkipCompetitors = async () => {
    console.log('[Onboarding] Skipping competitor selection')
    updateStepStatus(4, 'completed')
    setTimeout(() => advanceToNextStep(4), 300)
  }

  // STEP 5: Final Analysis (Auto-complete and redirect)
  const processFinalAnalysis = async () => {
    let uiStepInterval: number | null = null

    try {
      console.log('[Onboarding] Running final analysis...')

      // Reset UI step animation
      setAnalysisUIStep(0)
      analysisUIStepRef.current = 0

      // Start sequential UI animation (advance step every 3 seconds)
      uiStepInterval = setInterval(() => {
        if (analysisUIStepRef.current < 5) {
          analysisUIStepRef.current += 1
          setAnalysisUIStep(analysisUIStepRef.current)
          console.log(`[Onboarding] UI Animation Step: ${analysisUIStepRef.current}/5`)
        }
      }, 3000) // 3 seconds per step = 15 seconds total for 5 steps

      // Trigger the onboarding completion worker
      setAnalysisProgress({ current: 1, total: 100, task: 'Starting final analysis...' })
      console.log('[Onboarding] Triggering onboarding completion worker...')

      const triggerResponse = await api.post(`/onboarding/${activeBrand?.userBrandId}/complete-analysis`)

      if (!triggerResponse.data.success) {
        clearInterval(uiStepInterval)
        throw new Error('Failed to start onboarding completion')
      }

      console.log(`[Onboarding] Worker started (job: ${triggerResponse.data.jobId})`)

      // Poll for completion status (max 3 minutes)
      let backendCompleted = false
      let attempts = 0
      const maxAttempts = 90 // 90 * 2s = 3 minutes

      while (!backendCompleted && attempts < maxAttempts) {
        await new Promise(resolve => setTimeout(resolve, 2000))

        const statusRes = await api.get(`/onboarding/${activeBrand?.userBrandId}/completion-status`)
        const progress = statusRes.data.progress || 0

        // Update progress bar with descriptive stages
        if (progress < 10) {
          setAnalysisProgress({ current: progress, total: 100, task: 'Initializing analysis...' })
        } else if (progress < 25) {
          setAnalysisProgress({ current: progress, total: 100, task: 'Discovering market categories...' })
        } else if (progress < 50) {
          setAnalysisProgress({ current: progress, total: 100, task: 'Analyzing search query intents...' })
        } else if (progress < 65) {
          setAnalysisProgress({ current: progress, total: 100, task: 'Calculating AI Index Score...' })
        } else if (progress < 80) {
          setAnalysisProgress({ current: progress, total: 100, task: 'Analyzing competitive positioning...' })
        } else if (progress < 90) {
          setAnalysisProgress({ current: progress, total: 100, task: 'Researching market position...' })
        } else if (progress < 100) {
          setAnalysisProgress({ current: progress, total: 100, task: 'Finalizing your dashboard...' })
        } else {
          setAnalysisProgress({ current: progress, total: 100, task: 'Analysis complete!' })
        }

        console.log(`[Onboarding] Progress: ${progress}% (${statusRes.data.status})`)

        if (statusRes.data.completed) {
          backendCompleted = true
          console.log('[Onboarding] Backend processing completed!')
        }

        attempts++
      }

      if (!backendCompleted) {
        console.warn('[Onboarding] Worker timed out, but continuing to dashboard')
      }

      // Backend complete - immediately finish UI animation
      console.log('[Onboarding] Backend complete. Finishing UI animation...')
      clearInterval(uiStepInterval)
      analysisUIStepRef.current = 5
      setAnalysisUIStep(5)

      console.log('[Onboarding] All metrics calculated and UI animation complete!')
      updateStepStatus(5, 'completed')

      // Track onboarding completed
      trackEvent(ANALYTICS_EVENTS.ONBOARDING_COMPLETED, {
        brandId: activeBrand?.brandId || activeBrand?.id,
        industry: activeBrand?.industry,
        queriesCreated: selectedQueries.size,
        competitorsSelected: selectedCompetitors.size
      })

      // Navigate to dashboard after brief pause
      setTimeout(() => {
        navigate('/dashboard')
      }, 1500)
    } catch (error: any) {
      console.error('[Onboarding] Final analysis failed:', error)

      // Clear UI animation interval
      if (uiStepInterval) {
        clearInterval(uiStepInterval)
      }

      // Still redirect to dashboard even if analysis fails
      await saveProgress(5, true)

      setTimeout(() => {
        navigate('/dashboard')
      }, 2000)
    }
  }

  // Retry handler for failed steps
  const handleRetryStep = (stepIndex: number) => {
    console.log(`[Onboarding] Retrying step ${stepIndex}`)
    updateStepStatus(stepIndex, 'idle')
    stepProcessingStarted.current.delete(stepIndex)
    processStep(stepIndex)
  }

  const handleSkipStep = (stepIndex: number) => {
    console.log(`[Onboarding] Skipping step ${stepIndex}`)
    updateStepStatus(stepIndex, 'completed')
    setTimeout(() => advanceToNextStep(stepIndex), 300)
  }

  // ============================================================================
  // UI HELPERS
  // ============================================================================

  const toggleQuerySelection = (index: number) => {
    setSelectedQueries(prev => {
      const newSet = new Set(prev)
      if (newSet.has(index)) {
        newSet.delete(index)
      } else {
        // Cap at 5 queries max
        if (newSet.size < 5) {
          newSet.add(index)
        } else {
          toast.warning('Maximum 5 queries can be selected')
        }
      }
      return newSet
    })
  }

  const toggleCompetitor = (competitorId: string) => {
    setSelectedCompetitors(prev => {
      const newSet = new Set(prev)
      const maxSelection = Math.min(3, competitors.length)

      if (newSet.has(competitorId)) {
        newSet.delete(competitorId)
      } else if (newSet.size < maxSelection) {
        newSet.add(competitorId)
      } else {
        toast.warning(`Maximum ${maxSelection} competitor${maxSelection === 1 ? '' : 's'} can be selected`)
      }
      return newSet
    })
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 to-purple-50">
      <div className="max-w-4xl mx-auto px-4 py-6">
        {/* Header */}
        <div className="text-center mb-12">
          <div className="flex justify-center mb-4">
            <TrendingUp className="h-16 w-16 text-indigo-600" />
          </div>
          <h1 className="text-4xl font-bold text-gray-900 mb-3">
            Welcome to BrandIndex.ai
          </h1>
          <p className="text-lg text-gray-600">
            Let's set up your brand for AI visibility tracking
          </p>
        </div>

        {/* Progress Indicator */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-gray-700">
              Step {currentStep + 1} of {steps.length}
            </span>
            <span className="text-sm text-indigo-600 font-medium">
              {Math.round(((currentStep) / steps.length) * 100)}% Complete
            </span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-2">
            <div
              className="bg-indigo-600 h-2 rounded-full transition-all duration-500"
              style={{ width: `${((currentStep) / steps.length) * 100}%` }}
            />
          </div>
        </div>

        {/* Steps Container */}
        <div className="bg-white rounded-lg shadow-xl py-8 px-4 sm:px-10">
          <div className="space-y-6">
            {steps.map((step, index) => (
              <div
                key={step.id}
                className={`${
                  index === currentStep ? 'opacity-100' : index < currentStep ? 'opacity-60' : 'opacity-40'
                }`}
              >
                <div className="flex items-start space-x-3">
                  <div className="flex-shrink-0 mt-0.5">
                    {step.status === 'completed' ? (
                      <Check className="h-6 w-6 text-green-600" />
                    ) : step.status === 'processing' ? (
                      <Loader2 className="h-6 w-6 text-indigo-600 animate-spin" />
                    ) : step.status === 'error' ? (
                      <AlertCircle className="h-6 w-6 text-red-600" />
                    ) : (
                      <Circle className="h-6 w-6 text-gray-400" />
                    )}
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center justify-between">
                      <div className="flex-1">
                        <div className="flex items-center space-x-2">
                          <p className={`text-base font-semibold ${
                            step.status === 'completed' ? 'text-green-700' :
                            step.status === 'processing' ? 'text-indigo-700' :
                            step.status === 'error' ? 'text-red-700' :
                            index === currentStep ? 'text-gray-900' :
                            'text-gray-500'
                          }`}>
                            {step.title}
                          </p>
                          {step.status === 'processing' && (
                            <span className="px-2 py-0.5 bg-indigo-100 text-indigo-700 rounded text-xs font-medium">
                              Processing...
                            </span>
                          )}
                          {step.status === 'ready' && (
                            <span className="px-2 py-0.5 bg-green-100 text-green-700 rounded text-xs font-medium">
                              Ready
                            </span>
                          )}
                        </div>
                        <p className="text-sm text-gray-500 mt-0.5">{step.description}</p>
                        {step.error && (
                          <p className="text-sm text-red-600 mt-1">Error: {step.error}</p>
                        )}
                      </div>

                    </div>

                    {/* STEP CONTENT */}
                    {index === currentStep && (
                      <>
                        {/* Brand Learning Step */}
                        {step.id === 'brand-learning' && (
                          <div className="mt-4 space-y-3">
                            {step.status === 'processing' && (
                              <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
                                <div className="flex items-center space-x-3">
                                  <Loader2 className="h-5 w-5 text-purple-600 animate-spin" />
                                  <div className="flex-1">
                                    <span className="text-sm font-medium text-gray-900">
                                      Analyzing your brand site and related content...
                                    </span>
                                    <p className="text-xs text-gray-600 mt-0.5">
                                      Getting your brand value prop & benefits, understanding your market, identifying real-user search query types
                                    </p>
                                  </div>
                                </div>
                              </div>
                            )}

                          </div>
                        )}

                        {/* Confirm Brand Step */}
                        {step.id === 'confirm-brand' && (
                          <div className="mt-4">
                            {step.status === 'ready' && (
                              <BrandConfirmationStep
                                brandName={brandProfile.companyName}
                                brandDescription={brandProfile.description}
                                initialScope={businessScope}
                                initialServiceAreas={serviceAreas}
                                initialPrimaryLocation={primaryLocation}
                                onSave={handleSaveConfirmBrand}
                                isProcessing={false}
                              />
                            )}
                            {step.status === 'processing' && (
                              <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-4">
                                <div className="flex items-center space-x-3">
                                  <Loader2 className="h-5 w-5 text-indigo-600 animate-spin" />
                                  <div>
                                    <p className="text-sm font-medium text-gray-700">
                                      Saving brand details and preparing search queries...
                                    </p>
                                    <p className="text-xs text-gray-500 mt-1">
                                      This may take a few seconds
                                    </p>
                                  </div>
                                </div>
                              </div>
                            )}
                          </div>
                        )}

                        {/* Query Selection Step */}
                        {step.id === 'select-queries' && (
                          <div className="mt-4">
                            {step.status === 'processing' && recommendedQueries.length === 0 && (
                              <div className="space-y-2">
                                <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-4">
                                  <div className="flex items-center space-x-3">
                                    <Loader2 className="h-5 w-5 text-indigo-600 animate-spin" />
                                    <div>
                                      <p className="text-sm font-medium text-gray-700">
                                        Generating smart query recommendations...
                                      </p>
                                      <p className="text-xs text-gray-500 mt-1">
                                        Based on your brand profile
                                      </p>
                                    </div>
                                  </div>
                                </div>
                                {[1, 2, 3, 4, 5].map((i) => (
                                  <div key={i} className="p-3 border border-gray-200 rounded-lg animate-pulse">
                                    <div className="flex items-center space-x-3">
                                      <div className="w-5 h-5 bg-gray-200 rounded"></div>
                                      <div className="h-4 bg-gray-200 rounded flex-1"></div>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            )}

                            {(step.status === 'ready' || step.status === 'processing') && recommendedQueries.length > 0 && (
                              <>
                                <div className="space-y-2 mb-4">
                                  {recommendedQueries.map((query, queryIndex) => (
                                    <div
                                      key={queryIndex}
                                      onClick={() => toggleQuerySelection(queryIndex)}
                                      className={`p-3 border rounded-lg cursor-pointer transition-all ${
                                        selectedQueries.has(queryIndex)
                                          ? 'border-indigo-500 bg-indigo-50'
                                          : 'border-gray-200 hover:border-gray-300'
                                      }`}
                                    >
                                      <div className="flex items-center space-x-3">
                                        {selectedQueries.has(queryIndex) ? (
                                            <CheckCircle2 className="h-5 w-5 text-indigo-600" />
                                          ) : (
                                            <div className={"flex-shrink-0 w-5 h-5 rounded-full border-2 border-gray-300"}></div>
                                        )}
                                        <p className="text-sm font-medium text-gray-900">{query}</p>
                                      </div>
                                    </div>
                                  ))}
                                </div>

                                <div className="flex items-center justify-between pt-3 border-t">
                                  <span className="text-sm text-gray-600">
                                    {selectedQueries.size} of {recommendedQueries.length} selected (min 3, max 5)
                                  </span>
                                  <button
                                    onClick={handleCreateQueries}
                                    disabled={selectedQueries.size < 3 || step.status === 'processing'}
                                    className="flex items-center justify-center px-6 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                                  >
                                    {step.status === 'processing' ? (
                                      <>
                                        <Loader2 className="h-5 w-5 mr-2 animate-spin" />
                                        Creating...
                                      </>
                                    ) : (
                                      <>
                                        Create {selectedQueries.size} {selectedQueries.size === 1 ? 'Query' : 'Queries'}
                                        <ArrowRight className="h-5 w-5 ml-2" />
                                      </>
                                    )}
                                  </button>
                                </div>
                              </>
                            )}
                          </div>
                        )}

                        {/* Query Analysis Step */}
                        {step.id === 'analyze-queries' && (
                          <div className="mt-4">
                            {step.status === 'processing' && queryAnalysisProgress.queries.length > 0 && (
                              <div className="space-y-4">

                                {/* Query checklist with provider progress */}
                                <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-4">
                                  <p className="text-xs font-medium text-gray-700 mb-3">Analyzing Queries:</p>
                                  <div className="space-y-3">
                                    {queryAnalysisProgress.queries.map((queryItem, idx) => (
                                      <div key={idx} className="space-y-1.5">
                                        <div className="flex items-start space-x-2">
                                          {idx < queryAnalysisProgress.currentQuery ? (
                                            <Check className="h-4 w-4 text-green-600 mt-0.5 flex-shrink-0" />
                                          ) : idx === queryAnalysisProgress.currentQuery ? (
                                            <Loader2 className="h-4 w-4 text-indigo-600 animate-spin mt-0.5 flex-shrink-0" />
                                          ) : (
                                            <Circle className="h-4 w-4 text-gray-300 mt-0.5 flex-shrink-0" />
                                          )}
                                          <span className={`text-xs flex-1 ${
                                            idx <= queryAnalysisProgress.currentQuery ? 'text-gray-900 font-medium' : 'text-gray-500'
                                          }`}>
                                            "{queryItem.query}"
                                          </span>
                                        </div>
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              </div>
                            )}
                            {step.status === 'error' && (
                              <div className="flex space-x-3">
                                <button
                                  onClick={() => handleRetryStep(index)}
                                  className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700"
                                >
                                  Retry
                                </button>
                                <button
                                  onClick={() => handleSkipStep(index)}
                                  className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300"
                                >
                                  Skip This Step
                                </button>
                              </div>
                            )}
                          </div>
                        )}

                        {/* Competitor Selection Step */}
                        {step.id === 'select-competitors' && (
                          <div className="mt-4">
                            {step.status === 'processing' && competitors.length === 0 && (
                              <div className="space-y-2">
                                <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-4">
                                  <div className="flex items-center space-x-3">
                                    <Loader2 className="h-5 w-5 text-indigo-600 animate-spin" />
                                    <div>
                                      <p className="text-sm font-medium text-gray-700">
                                        Analyzing your competitors...
                                      </p>
                                      <p className="text-xs text-gray-500 mt-1">
                                        Finding similar brands in your industry
                                      </p>
                                    </div>
                                  </div>
                                </div>
                                {[1, 2, 3, 4, 5].map((i) => (
                                  <div key={i} className="p-3 border border-gray-200 rounded-lg animate-pulse">
                                    <div className="flex items-center justify-between">
                                      <div className="flex-1">
                                        <div className="h-4 bg-gray-200 rounded w-1/3 mb-2"></div>
                                        <div className="h-3 bg-gray-200 rounded w-1/2"></div>
                                      </div>
                                      <div className="w-5 h-5 bg-gray-200 rounded"></div>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            )}

                            {step.status === 'ready' && competitors.length > 0 && (
                              <>
                                <div className="grid grid-cols-1 md:grid-cols-3 gap-2 mb-4 max-h-96 overflow-y-auto">
                                  {competitors.map(competitor => (
                                    <div
                                      key={competitor.id}
                                      onClick={() => toggleCompetitor(competitor.id)}
                                      className={`p-3 border rounded-lg cursor-pointer transition-all ${
                                        selectedCompetitors.has(competitor.id)
                                          ? 'border-indigo-500 bg-indigo-50'
                                          : 'border-gray-200 hover:border-gray-300'
                                      }`}
                                    >
                                      <div className="flex items-start space-x-3">
                                        {selectedCompetitors.has(competitor.id) ? (
                                            <CheckCircle2 className="h-5 w-5 text-indigo-600" />
                                        ) : (
                                            <div className={"flex-shrink-0 w-5 h-5 rounded-full border-2 border-gray-300"}></div>
                                        )}
                                        <div>
                                          <p className="font-medium text-gray-900 text-sm">{competitor.name}</p>
                                          <p className="text-xs text-gray-500">{competitor.domain}</p>
                                        </div>
                                      </div>

                                    </div>
                                  ))}
                                </div>

                                <div className="flex items-center justify-between pt-3 border-t">
                                  <span className="text-sm text-gray-600">
                                    {selectedCompetitors.size} of {Math.min(3, competitors.length)} selected
                                  </span>
                                  <button
                                    onClick={handleSaveCompetitors}
                                    disabled={selectedCompetitors.size !== Math.min(3, competitors.length)}
                                    className="flex items-center justify-center px-6 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                                  >
                                    Continue
                                    <ArrowRight className="h-5 w-5 ml-2" />
                                  </button>
                                </div>
                              </>
                            )}

                            {step.status === 'ready' && competitors.length === 0 && (
                              <div className="text-center py-6">
                                <AlertCircle className="h-8 w-8 text-yellow-600 mx-auto mb-2" />
                                <p className="text-sm text-gray-700 mb-4">
                                  We couldn't find any competitors automatically.
                                </p>
                                <button
                                  onClick={handleSkipCompetitors}
                                  className="px-6 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300"
                                >
                                  Skip and Continue
                                </button>
                              </div>
                            )}

                            {step.status === 'error' && (
                              <div className="text-center py-6">
                                <AlertCircle className="h-8 w-8 text-red-600 mx-auto mb-2" />
                                <p className="text-sm text-gray-700 mb-4">
                                  Failed to generate competitors
                                </p>
                                <div className="flex justify-center space-x-3">
                                  <button
                                    onClick={() => handleRetryStep(index)}
                                    className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700"
                                  >
                                    Retry
                                  </button>
                                  <button
                                    onClick={handleSkipCompetitors}
                                    className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300"
                                  >
                                    Skip This Step
                                  </button>
                                </div>
                              </div>
                            )}
                          </div>
                        )}

                        {/* Final Analysis Step */}
                        {step.id === 'final-analysis' && (
                          <div className="mt-4">
                            {step.status === 'processing' && (
                              <div className="space-y-4">
                                <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-4">
                                  <div className="flex items-center justify-between mb-3">
                                    <div className="flex items-center space-x-3">
                                      <Loader2 className="h-5 w-5 text-indigo-600 animate-spin" />
                                      <div>
                                        <p className="text-sm font-medium text-gray-700">
                                          {analysisProgress.task || 'Running complete analysis...'}
                                        </p>
                                        <p className="text-xs text-gray-500 mt-1">
                                          Step {analysisProgress.current} of {analysisProgress.total}
                                        </p>
                                      </div>
                                    </div>
                                    <span className="text-sm font-medium text-indigo-600">
                                      {Math.round((analysisProgress.current / analysisProgress.total) * 100)}%
                                    </span>
                                  </div>

                                  {/* Progress bar */}
                                  <div className="w-full bg-gray-200 rounded-full h-2">
                                    <div
                                      className="bg-indigo-600 h-2 rounded-full transition-all duration-500"
                                      style={{ width: `${(analysisProgress.current / analysisProgress.total) * 100}%` }}
                                    />
                                  </div>
                                </div>

                                {/* Checklist of metrics being calculated */}
                                <div className="bg-white border border-gray-200 rounded-lg p-4">
                                  <p className="text-xs font-medium text-gray-700 mb-2">Calculating Brand Metrics:</p>
                                  <div className="space-y-1.5">
                                    {/* Query Objectives */}
                                    <div className="flex items-center space-x-2">
                                      {analysisUIStep > 1 ? (
                                        <Check className="h-4 w-4 text-green-600" />
                                      ) : analysisUIStep === 1 ? (
                                        <Loader2 className="h-4 w-4 text-indigo-600 animate-spin" />
                                      ) : (
                                        <Circle className="h-4 w-4 text-gray-300" />
                                      )}
                                      <span className={`text-xs ${analysisUIStep >= 1 ? 'text-gray-900' : 'text-gray-500'}`}>
                                        Query Objectives
                                      </span>
                                    </div>
                                    {/* Search Categories */}
                                    <div className="flex items-center space-x-2">
                                      {analysisUIStep > 2 ? (
                                        <Check className="h-4 w-4 text-green-600" />
                                      ) : analysisUIStep === 2 ? (
                                        <Loader2 className="h-4 w-4 text-indigo-600 animate-spin" />
                                      ) : (
                                        <Circle className="h-4 w-4 text-gray-300" />
                                      )}
                                      <span className={`text-xs ${analysisUIStep >= 2 ? 'text-gray-900' : 'text-gray-500'}`}>
                                        Search Categories
                                      </span>
                                    </div>
                                    {/* Share of Voice */}
                                    <div className="flex items-center space-x-2">
                                      {analysisUIStep > 3 ? (
                                        <Check className="h-4 w-4 text-green-600" />
                                      ) : analysisUIStep === 3 ? (
                                        <Loader2 className="h-4 w-4 text-indigo-600 animate-spin" />
                                      ) : (
                                        <Circle className="h-4 w-4 text-gray-300" />
                                      )}
                                      <span className={`text-xs ${analysisUIStep >= 3 ? 'text-gray-900' : 'text-gray-500'}`}>
                                        Share of Voice
                                      </span>
                                    </div>
                                    {/* Sentiment */}
                                    <div className="flex items-center space-x-2">
                                      {analysisUIStep > 4 ? (
                                        <Check className="h-4 w-4 text-green-600" />
                                      ) : analysisUIStep === 4 ? (
                                        <Loader2 className="h-4 w-4 text-indigo-600 animate-spin" />
                                      ) : (
                                        <Circle className="h-4 w-4 text-gray-300" />
                                      )}
                                      <span className={`text-xs ${analysisUIStep >= 4 ? 'text-gray-900' : 'text-gray-500'}`}>
                                        Sentiment
                                      </span>
                                    </div>
                                    {/* AI Brand Index Score */}
                                    <div className="flex items-center space-x-2">
                                      {analysisUIStep >= 5 ? (
                                        <Check className="h-4 w-4 text-green-600" />
                                      ) : analysisUIStep === 5 ? (
                                        <Loader2 className="h-4 w-4 text-indigo-600 animate-spin" />
                                      ) : (
                                        <Circle className="h-4 w-4 text-gray-300" />
                                      )}
                                      <span className={`text-xs ${analysisUIStep >= 5 ? 'text-gray-900' : 'text-gray-500'}`}>
                                        AI Brand Index Score
                                      </span>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            )}
                          </div>
                        )}
                      </>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  )
}
